import {
  HEADER_UNIT_MAIN_YEAR,
  HEADER_UNIT_MAIN_MONTH,
  HEADER_UNIT_MAIN_WEEK,
  HEADER_UNIT_MAIN_DAY,
  HEADER_UNIT_SUB_HOUR,
  HEADER_UNIT_SUB_DAY,
  HEADER_UNIT_SUB_MONTH,
} from "./Const";
import {
  startOfYear,
  startOfMonth,
  startOfWeek,
  addDays,
  addYears,
  addMonths,
  addWeeks,
  addHours,
  format,
  isSameDay,
  isWeekend,
  subDays,
  subSeconds,
  addSeconds,
  parse,
  startOfDay,
} from "date-fns";
import { enGB, fi, de, sv } from "date-fns/locale";
import Holidays from "date-holidays";
import i18n from "../../i18n";
import { ensureStartAndEnd } from "../../backend/helpers/dateHelpers";
import { IHierarchyItem } from "src/backend/interfaces/IItem";

export const DATERANGE_SEPARATOR = " - ";

const hd = new Holidays("FI");

type WeekStartsOnType = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export function getNextRoundedDate(headerUnit: string, lastTime: Date, startOfWeekDay: number): Date {
  if (headerUnit === HEADER_UNIT_MAIN_YEAR) {
    return addYears(startOfYear(lastTime), 1);
  } else if (headerUnit === HEADER_UNIT_MAIN_MONTH || headerUnit === HEADER_UNIT_SUB_MONTH) {
    return addMonths(startOfMonth(lastTime), 1);
  } else if (headerUnit === HEADER_UNIT_MAIN_WEEK) {
    const startOfCurrentWeek = startOfWeek(lastTime, {
      weekStartsOn: startOfWeekDay !== undefined ? startOfWeekDay as WeekStartsOnType : 1 /* default: Monday */,
    });
    return addWeeks(startOfCurrentWeek, 1);
  } else if (headerUnit === HEADER_UNIT_MAIN_DAY || headerUnit === HEADER_UNIT_SUB_DAY) {
    return addDays(lastTime, 1);
  } else if (headerUnit === HEADER_UNIT_SUB_HOUR) {
    return addHours(lastTime, 1);
  }
}

export function getValidatedDatesFromInput(inputVal: string, dateFormat: string): { start?: Date, end?: Date, error?: string} {
  const dateStrs = inputVal.split(DATERANGE_SEPARATOR);

  const start = parseDate(dateStrs[0], dateFormat);
  const end = parseDate(dateStrs[1], dateFormat);
  if (start === null || !isFinite(Number(start)) || end === null || !isFinite(Number(end))) {
    return { error: `${dateFormat}-${dateFormat}` };
  }
  if (end <= start) {
    return { error: i18n.t("error.enddatebeforestartdate") };
  }
  return { start: start, end: end };
}

export function parseDate(dateStr: string, dateFormat: string): Date {
  return parse(dateStr, dateFormat, new Date(), { locale: currentLocale() });
}

export function getFormattedDateText(startDate: Date, endDate: Date, dateFormat: string): string {
  return formatWithLocale(startDate, dateFormat) + DATERANGE_SEPARATOR + formatWithLocale(endDate, dateFormat);
}

/**
 * counts the duration of a task in actual (non weekend/holiday) working days, rounded up on both ends,
 * ie. timespan of Jan 1st 2020 23:59 - Jan 2nd 2020 0:00 is two days.
 * @returns number of working days. Minimum 1 day.
 */
function getWorkingDayCount(start: Date, end: Date): number {
  let rawWorkingDays = 0;
  let iterDay = new Date(start.getTime());
  while (iterDay <= end || isSameDay(iterDay, end)) {
    if (!isWeekend(iterDay) && !isHoliday(iterDay)) {
      rawWorkingDays++;
    }
    iterDay = addDays(iterDay, 1);
  }
  return Math.max(rawWorkingDays, 1);
}

export const findWorkingDayAdjustedTimeRange = (task: IHierarchyItem, tentativeStart: Date, tentativeEnd: Date, adjustEnd: boolean): { newStart: Date, newEnd: Date } => {
  // subtract one second from end time for working day calculation purposes due to 0:00 time special
  // case otherwise counting as an extra work day.
  const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration);
  tentativeEnd = subSeconds(tentativeEnd, 1);
  const totalActualWorkingDaysRequired = getWorkingDayCount(validStart, subSeconds(validEnd, 1));
  let tentativeActualWorkingDays = getWorkingDayCount(tentativeStart, tentativeEnd);
  if (adjustEnd) {
    while (tentativeActualWorkingDays > totalActualWorkingDaysRequired) {
      tentativeEnd = subDays(tentativeEnd, 1);
      if (!isHoliday(tentativeEnd) && !isWeekend(tentativeEnd)) {
        tentativeActualWorkingDays--;
      }
    }
    while (tentativeActualWorkingDays < totalActualWorkingDaysRequired) {
      tentativeEnd = addDays(tentativeEnd, 1);
      if (!isHoliday(tentativeEnd) && !isWeekend(tentativeEnd)) {
        tentativeActualWorkingDays++;
      }
    }

    // Trim unnecessary non-working days from the end.
    while (isWeekend(tentativeEnd) || isHoliday(tentativeEnd)) {
      tentativeEnd = subDays(tentativeEnd, 1);
    }
  } else {
    while (tentativeActualWorkingDays < totalActualWorkingDaysRequired) {
      tentativeStart = subDays(tentativeStart, 1);
      if (!isHoliday(tentativeStart) && !isWeekend(tentativeStart)) {
        tentativeActualWorkingDays++;
      }
    }
    while (tentativeActualWorkingDays > totalActualWorkingDaysRequired) {
      tentativeStart = addDays(tentativeStart, 1);
      if (!isHoliday(tentativeStart) && !isWeekend(tentativeStart)) {
        tentativeActualWorkingDays--;
      }
    }

    // Trim unnecessary non-working days from the beginning.
    while (isWeekend(tentativeStart) || isHoliday(tentativeStart)) {
      tentativeStart = addDays(tentativeStart, 1);
    }
  }
  tentativeEnd = addSeconds(tentativeEnd, 1);
  return { newStart: tentativeStart, newEnd: tentativeEnd };
};

export function isHoliday(dateObj: Date): boolean {
  return hd.isHoliday(dateObj) !== false;
}

export const currentLocale = (): Locale => {
  switch(i18n.language) {
    case 'de':
      return de;
    case 'fi':
      return fi;
    case 'sv':
      return sv;
    default:
      return enGB;
  }
}

const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export const getMonthNames = (options: unknown): string[] => {
  const localize = currentLocale().localize;
  const months = [];
  for(let month = 0; month < 12; month++) {
    months[month] = capitalize(localize.month(month, options));
  }
  return months;
}

//January, February, ...
export const getLongMonthNames = (): string[] => {
  return getMonthNames({ width: "wide", context: "standalone" });
}

//Jan, Feb, ...
export const getShortMonthNames = (): string[] => {
  return getMonthNames({ width: "abbreviated", context: "standalone" });
}

export const getDayNames = (options: unknown): string[] => {
  const localize = currentLocale().localize;
  const days = [];
  for(let day = 0; day < 7; day++) {
    days[day] = capitalize(localize.day(day, options));
  }
  return days;
}

//Mo, Tu, ...
export const getShortDayNames = (): string[] => {
  return getDayNames({ width: "short", context: "standalone" });
}

//Monday, Tuesday, ...
export const getLongDayNames = (): string[] => {
  return getDayNames({ width: "wide", context: "standalone" });
}

//react-date-range doesn't work properly with locales from date-fns, 
//this returns minimum required locale for react-date-range
export const dateRangeCurrentLocale = (): unknown => {

  const months = getLongMonthNames();
  const days = getShortDayNames();

  return {
    localize: {
      month: n => months[n],
      months: () => months,
      day: n => days[n]
    },
    formatLong: {}
  };
}

export const formatWithLocale = (date: Date, formatStr = "PP"): string => {
  return format(date, formatStr, { locale: currentLocale() });
};